import { Language, LanguageSingleton } from '@lessonup/typescript-core/domain';
import {
  channelOverviewRoute,
  channelRoute,
  ChannelRouteParams,
  lessonRoute,
  lessonSearchRoute,
  aboutLessonUpRoute,
  aiTools,
  channelListRoute,
  editorUrl,
  editorV2Url,
  editPlanRoute,
  teacherMyLessonsUrl,
} from '@lessonup/typescript-core/services';
import { BetaTag, Link, styled, TopBarItem } from '@lessonup/ui-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { matchPath, useLocation } from 'react-router';
import {
  activeDocumentForUser,
  featureFlagsForUser,
  geoLocation as geoLocationSelector,
  isUserSignedIn,
  loggedInUser,
  schoolForUser,
} from '../../redux/selectors';
import {
  channelRouterRoute,
  meteorRouteWithLanguageQueryParam,
  routerSearchLessonsRoute,
  routerSearchPlanRoute,
} from '../../utils/routeUtils/routeUtils';
import { useAppServices } from '../appServices/AppServicesContext';
import { LoginPromptAction } from '../modals/LoginPromptModal/LoginPromptModal';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- auto-generated TECH-258
interface Props {}

type NavData = {
  url: string;
  external: boolean; // external to search (not LessonUp)
  translationKey?: string;
  translationNamespace?: string;
  requireLogin?: LoginPromptAction;
  showBetaTag?: boolean;
};

function navigationLinks(language?: Language.Key, country?: string): Record<NavLink, NavData> {
  return {
    help: { url: aboutLessonUpRoute(), external: true },
    channels: { url: meteorRouteWithLanguageQueryParam(channelListRoute(), language), external: false },
    myLessonUp: { url: teacherMyLessonsUrl(), external: true },
    search: { url: lessonSearchRoute(country ? { country } : {}), external: false },
    activeDocumentTab: {
      url: '',
      external: true,
    },
    mySchool: { url: '', external: false },
    aiTools: {
      url: aiTools(language),
      external: true,
      translationNamespace: 'topbar',
      translationKey: 'aiToolsTab',
      requireLogin: 'aiTools',
    },
  };
}

export type NavLink =
  | 'help'
  | 'channels'
  | 'myLessonUp'
  | 'activeDocumentTab'
  | 'mySchool'
  | 'search'
  | 'mySchool'
  | 'aiTools';

let fullUrls: NavLink[] = ['help', 'channels', 'myLessonUp', 'activeDocumentTab', 'mySchool'];

// eslint-disable-next-line no-empty-pattern -- auto-generated TECH-258
export default function Navigation({}: Props) {
  const { t } = useTranslation('navbar');
  const isSignedIn = useSelector(isUserSignedIn());
  const userSchool = useSelector(schoolForUser());
  const location = useLocation();
  const userActiveLesson = useSelector(activeDocumentForUser());
  const user = useSelector(loggedInUser());
  const geoLocation = useSelector(geoLocationSelector());
  const country = user?.country || geoLocation?.countryCode || undefined;
  const navContent = navigationLinks(LanguageSingleton.get(), country);

  // Elsewhere in the code, we use a GraphQL query to fetch the feature flags for the user.
  // We don't do that here, because we don't have an Apollo client in search and settings that up would require significant effort.
  // Also, search can be server side rendered and the Apollo approach would only work client side.
  // Long story short, we keep using the old approach here.
  const flags = useSelector(featureFlagsForUser());
  const showAiTools = flags.includes('aiToolboxOverview');

  const services = useAppServices();
  // eslint-disable-next-line no-extra-boolean-cast -- auto-generated TECH-258
  const navLinks: NavLink[] = !!isSignedIn
    ? ['myLessonUp', 'activeDocumentTab', 'search', 'channels']
    : ['help', 'search', 'channels'];

  if (showAiTools) {
    navLinks.push('aiTools');
  }

  if (userSchool) {
    navContent.mySchool.url = channelRoute({
      channelSlug: userSchool.slug,
      language: LanguageSingleton.get(),
    });
    navLinks.push('mySchool');
  }

  if (isSignedIn) {
    if (userActiveLesson?.type === 'lesson') {
      navContent.activeDocumentTab.url = editorUrl(userActiveLesson.lessonId, userActiveLesson.pinId);
      navContent.activeDocumentTab.translationKey = 'myLessonTab';
    }
    if (userActiveLesson?.type === 'lessonV2') {
      navContent.activeDocumentTab.url = editorV2Url(userActiveLesson.lessonId, userActiveLesson.pinId);
      navContent.activeDocumentTab.translationKey = 'myLessonTab';
    }
    if (userActiveLesson?.type === 'plan') {
      navContent.activeDocumentTab.url = editPlanRoute(userActiveLesson.planId);
      navContent.activeDocumentTab.translationKey = 'myLessonPlanTab';
    }
  }

  navContent.channels.url = channelOverviewRoute();
  fullUrls = fullUrls.filter((url) => url !== 'channels');

  const language = LanguageSingleton.get();

  function pathWithCorrectLanguage(path: string): string {
    return Language.isMain(LanguageSingleton.get())
      ? path
      : Language.changePathLanguage(path, Language.fallbackForLanguage(language));
  }

  return (
    <>
      {navLinks.map((navLink) => {
        const { url, external, translationKey, translationNamespace, requireLogin, showBetaTag } = navContent[navLink];
        if (!url) return;

        const path = pathWithCorrectLanguage(url);
        const active = tabIsActive(navLink, location.pathname, userSchool?.slug);

        const onNavigate = async () => {
          if (requireLogin && !isSignedIn) {
            const loginResponse = await services.modals.open('loginPrompt', { action: requireLogin });
            if (loginResponse.dismiss) return true;
          }
          return false;
        };

        return (
          <TopBarItem key={navLink} active={active}>
            <Link onClick={onNavigate} href={path} passHref external={external}>
              <a>
                {t(translationKey || navLink, { ns: translationNamespace })}
                {showBetaTag && <StyledBetaTag />}
              </a>
            </Link>
          </TopBarItem>
        );
      })}
    </>
  );
}

// This is to visually align the beta tag with the text
const StyledBetaTag = styled(BetaTag)`
  margin: 7px 0 8px 4px;
`;

function tabIsActive(navLink: NavLink, locationPathName: string, schoolSlug?: string) {
  let path: string | string[] | undefined;

  switch (navLink) {
    case 'channels':
      path = [channelRouterRoute(), channelOverviewRoute()];
      break;
    case 'search':
      path = [routerSearchLessonsRoute(), routerSearchPlanRoute(), lessonRoute(':id', ':pinId?')];
      break;
    case 'mySchool':
      path = schoolSlug ? [channelRouterRoute()] : undefined;
      break;
    default:
      path = navigationLinks()[navLink].url;
      break;
  }

  const match = matchPath(locationPathName, {
    path,
    exact: true,
  });
  if (schoolSlug) {
    const currentSlugIsSchoolSlug = (match?.params as ChannelRouteParams)?.channelSlug === schoolSlug;

    if (navLink === 'channels' && currentSlugIsSchoolSlug) {
      // if the school channel is active, disable channels tab
      return false;
    }
    if (navLink === 'mySchool' && !currentSlugIsSchoolSlug) {
      // if we only match the channel route if the slug is the same
      return false;
    }
  }

  return !!match;
}
